import AIState, { useAIState } from "@/components/AIState";
import { submitAI, refreshAI } from "@/lib/aiOperations";
import { useRead, ReadState, isCollection, isTrends } from "@/components/LoadingStates";
import { useAuth } from "@/context/AuthContext";
import IncludeDemoToggle from "@/components/IncludeDemoToggle";
import useIncludeDemo from "@/hooks/useIncludeDemo";
import { useCallback, useEffect, useRef, useState } from "react";
import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radar, Flame, Zap, Eye, Sparkles, Loader2 } from "lucide-react";
import { formatNumber, classNames } from "@/lib/format";
import MetricCard from "@/components/MetricCard";
import TrendCluster from "@/components/TrendCluster";
import TrendBrief from "@/components/TrendBrief";
import VideoCard from "@/components/VideoCard";
import ShareButton from "@/components/ShareButton";
import PlatformBadge from "@/components/PlatformBadge";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia } from "@/lib/media";
import { Link } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const WEEKS = [
  { key: 0, label: "This week" },
  { key: 1, label: "Last week" },
  { key: 2, label: "2 weeks ago" },
  { key: 3, label: "3 weeks ago" },
];

function delta(cur, prev, suffix = "") {
  if (!prev) return cur ? { text: "New this week", positive: true } : null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { text: `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct)}% vs prev week${suffix}`, positive: pct >= 0 };
}

export default function Trends() {
  const { canEdit, user } = useAuth();
  const [includeDemo, setIncludeDemo] = useIncludeDemo(user?.workspace_id);
  const [week, setWeek] = useState(0);
  const watchlistsRead = useRead("/watchlists", isCollection);
  const watchlists = watchlistsRead.data || [];
  const [watchlistId, setWatchlistId] = useState("all");
  const reportRead = useRead(`/trends/weekly?week_offset=${week}${watchlistId !== "all" ? `&watchlist_id=${watchlistId}` : ""}`, isTrends);
  const report = reportRead.data;
  const loading = reportRead.status === "loading";
  const ai = useAIState("trends_brief");
  const operationScope = `${user?.id}|${user?.workspace_id || "personal"}|${week}|${watchlistId}`;
  const operationScopeRef = useRef(operationScope); operationScopeRef.current = operationScope;
  const [operationState, setOperationState] = useState(null);
  const operation = operationState?.scope === operationScope ? operationState.data : null;
  const setOperation = (data) => { if (operationScopeRef.current === operationScope) setOperationState({ scope: operationScope, data }); };
  const refreshOperation = () => refreshAI(user, "trends-brief").then(setOperation).catch(() => {});
  const cachedRead = useRead(`/trends/weekly/brief?week_offset=${week}${watchlistId !== "all" ? `&watchlist_id=${watchlistId}` : ""}`, d => d === null || (d && typeof d.headline === "string"));
  const [briefState, setBriefState] = useState(null);
  const briefKey = `${user?.workspace_id || "personal"}|${week}|${watchlistId}`;
  const briefCurrent = useRef(briefKey); briefCurrent.current = briefKey;
  const brief = briefState?.key === briefKey ? briefState.data : null;
  const setBrief = useCallback((data) => setBriefState(data ? { key: briefKey, data } : null), [briefKey]);
  const [briefLoading, setBriefLoading] = useState(false);
  useEffect(() => { setWatchlistId("all"); }, [user?.workspace_id]);
  useEffect(() => { setBrief(null); }, [week, watchlistId, setBrief]);

  useEffect(() => { if (cachedRead.data) setBrief(cachedRead.data); }, [cachedRead.data, setBrief]);

  const generateBrief = async () => {
    if (!canEdit || briefLoading || (!ai.allowed && !["generated", "saving"].includes(operation?.state)) || reportRead.status !== "success") return;
    setBriefLoading(true);
    try {
      const wl = watchlistId !== "all" ? `&watchlist_id=${watchlistId}` : "";
      const data = await submitAI(user, "trends-brief", `/trends/weekly/brief?week_offset=${week}${wl}`, { week_offset: week, watchlist_id: watchlistId }, { standard: ai.standard });
      if (briefCurrent.current === briefKey) setBrief(data);
    } catch (e) {
      toast.error(formatError(e));
      refreshOperation();
    } finally {
      setBriefLoading(false);
      ai.read.reload().catch(() => {});
    }
  };

  const k = report?.kpis;

  return (
    <div className="space-y-6" data-testid="trends-page">
      <SectionTabs tabs={SECTION_TABS.signals} activeKey="trends" ariaLabel="Signals sections" data-testid="section-tabs-signals" />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Radar className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Trend Radar</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Hooks and topics catching fire across your watched creators, week over week.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {report && !loading && <ShareButton kind="trend" params={{ week_offset: week, watchlist_id: watchlistId !== "all" ? watchlistId : null }} />}
<ReadState read={watchlistsRead} scope="trends-watchlists"><Select value={watchlistId} onValueChange={setWatchlistId}>
            <SelectTrigger className="w-[200px]" data-testid="trends-watchlist-select">
              <SelectValue placeholder="All creators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="trends-watchlist-all">All creators</SelectItem>
              {watchlists.map((w) => (
                <SelectItem key={w.id} value={w.id} data-testid={`trends-watchlist-${w.id}`}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select></ReadState>
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            {WEEKS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWeek(w.key)}
                data-testid={`trends-week-${w.key}`}
                className={classNames(
                  "px-3 py-1.5 text-xs rounded-md transition-colors font-medium",
                  week === w.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <IncludeDemoToggle checked={includeDemo} onChange={setIncludeDemo} unavailable />
      <p className="text-xs text-muted-foreground">Agrégats serveur — tous les contenus. L’origine des vidéos résumées n’est pas fournie.</p>
      <ReadState read={reportRead} scope="trends" empty={report?.kpis.videos === 0} title="Aucune vidéo publiée dans cette période" primaryCta={{ label: "Consulter les créateurs", to: "/creators" }} legacyEmptyId="trends-empty">
      {report && <>
          <div className="text-xs font-mono text-muted-foreground" data-testid="trends-window">
            {report.window.start.slice(0, 10)} → {report.window.end.slice(0, 10)}
          </div>

          <p className="text-xs text-muted-foreground">Vidéos uniques ; multiplicateurs et regroupements selon le profil de référence maximale.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard label="Videos published" value={k.videos} delta={delta(k.videos, k.videos_prev)} icon={Eye} accent="primary" testid="trends-kpi-videos" />
            <MetricCard label="Outliers detected" value={k.outliers} delta={delta(k.outliers, k.outliers_prev)} icon={Zap} accent="emerald" testid="trends-kpi-outliers" />
            <MetricCard label="Hottest hook" value={<span className="text-xl leading-tight block">{k.hottest_hook || "—"}</span>} icon={Flame} accent="rose" testid="trends-kpi-hook" />
            <MetricCard label="Hottest topic" value={<span className="text-xl leading-tight block">{k.hottest_topic || "—"}</span>} icon={Sparkles} accent="amber" testid="trends-kpi-topic" />
          </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="p-5 lg:col-span-2">
                  <h3 className="font-display font-bold text-lg mb-1">Daily pulse</h3>
                  <p className="text-xs text-muted-foreground mb-4">Videos published vs. outliers per day.</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={report.daily} barGap={2}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} width={28} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="videos" name="Videos" radius={[4, 4, 0, 0]} fill="hsl(var(--primary))" />
                        <Bar dataKey="outliers" name="Outliers" radius={[4, 4, 0, 0]} fill="hsl(var(--emerald))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <Card className="p-5" data-testid="trends-rising-creators">
                  <h3 className="font-display font-bold text-lg mb-1">Rising creators</h3>
                  <p className="text-xs text-muted-foreground mb-4">Most outliers this week.</p>
                  <div className="space-y-2">
                    {report.rising_creators.length === 0 && <div className="text-sm text-muted-foreground">No outliers yet this week.</div>}
                    {report.rising_creators.map((c, i) => (
                      <Link key={c.creator_id} to={`/creators/${c.creator_id}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent transition-colors" data-testid={`rising-creator-${c.creator_id}`}>
                        <span className="font-mono text-xs text-muted-foreground w-4">{i + 1}</span>
                        <MediaImage src={c.creator_avatar} alt="" name={c.creator_name || c.creator_handle} kind="avatar" demo={explicitDemoMedia(c)} className="h-7 w-7 rounded-full object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.creator_handle}</div>
                          <PlatformBadge platform={c.platform} size="xs" />
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-sm font-bold text-[hsl(var(--emerald))]">{c.outliers}×</div>
                          <div className="text-[10px] text-muted-foreground">peak {c.peak_multiplier.toFixed(1)}x</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TrendCluster title="Hooks catching fire" subtitle="Opening patterns ranked by views share × outlier density." clusters={report.hooks} accent="rose" testid="trends-hooks" />
                <TrendCluster title="Topics heating up" subtitle="Niches drawing the most attention this week." clusters={report.topics} accent="amber" testid="trends-topics" />
              </div>

              <Card className="p-5" data-testid="trends-brief-card">
                <AIState read={ai.read} scope="trends" operation={operation} onRefresh={refreshOperation} />
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold text-lg flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI weekly brief</h3>
                    <p className="text-xs text-muted-foreground mt-1">Claude Sonnet 5 reads the clusters and writes next week's playbook.</p>
                  </div>
                  {canEdit && !brief && (
                    <Button onClick={generateBrief} disabled={briefLoading || (!ai.allowed && !["generated", "saving"].includes(operation?.state))} data-testid="trends-generate-brief">
                      {briefLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Writing brief…</> : "Generate brief"}
                    </Button>
                  )}
                </div>
                {brief && <TrendBrief brief={brief} />}
              </Card>

              {report.breakouts.length > 0 && (
                <div data-testid="trends-breakouts">
                  <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Zap className="h-4 w-4 text-[hsl(var(--emerald))]" /> Breakouts of the week</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {report.breakouts.map((v) => <VideoCard key={v.id} video={v} compact originAvailable={false} />)}
                  </div>
                </div>
              )}
        </>}
      </ReadState>
    </div>
  );
}
